import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { filter, first, map } from "rxjs/operators";
import {
  AbstractUserService,
  EntityId,
  IRoleAdmin,
  IRoleEdit,
  IUser,
  UserId
} from "src/app/interfaces";
import { Constants } from "src/app/interfaces/constants";
import { LogClass, Logger, LogMethod } from "src/app/logger";
import { selectCurrentUser } from "../state";
import { UserActions } from "../state/actions";

@LogClass({ source: "UserFacade" })
@Injectable({
  providedIn: "root"
})
export class UserFacade {
  private logger: Logger;

  constructor(private store: Store, private userService: AbstractUserService) {
    this.logger.debug("ctor");
  }

  /**
   * Gibt den aktuellen User zurück
   *
   * @returns {Observable<IUser>}
   * @memberof UserFacade
   */
  @LogMethod()
  public getCurrentUser(): Observable<IUser> {
    return this.store.select(selectCurrentUser);
  }

  /**
   * Gibt den aktuellen User zurück oder den per UserId spezifizierten.
   *
   * @param {UserId} [userId]
   * @returns {Observable<IUser>}
   * @memberof UserFacade
   */
  @LogMethod()
  public getUser(userId?: UserId): Observable<IUser> {
    if (userId) {
      return this.userService.getUser(userId);
    }

    return this.getCurrentUser();
  }

  /**
   * Gibt die UserId aus der Route zurück
   *
   * @param {UserId} [userId]
   * @returns {Observable<IUser>}
   * @memberof UserFacade
   */
  @LogMethod()
  public getUserIdFromRoute(route: ActivatedRoute): Observable<number> {
    return route.params.pipe(
      map((params) => Number(params[Constants.routeParams.userId])),
      filter((id) => !!id),
      first()
    );
  }
  /**
   * Gibt alle Rollen zurük und markiert die, die der User hat
   *
   * @param {UserId} [userId]
   * @returns {Observable<IUser>}
   * @memberof UserFacade
   */
  @LogMethod()
  public getUserRole(userId: UserId): Observable<IRoleEdit[]> {
    return this.userService.getUserRole(userId);
  }
  @LogMethod()
  public getRoles(): Observable<IRoleAdmin[]> {
    return this.userService.getRoles();
  }
  @LogMethod()
  public getUserByLogin(login: string): Observable<IUser> {
    return this.userService.getUsers([login]).pipe(map((users) => users[0]));
  }

  @LogMethod()
  public saveUserRoles(
    userId: EntityId,
    roles: IRoleEdit[]
  ): Observable<boolean> {
    return this.userService.saveUserRoles(userId, roles);
  }

  @LogMethod()
  public saveRoles(roles: IRoleAdmin[]): Observable<boolean> {
    return this.userService.saveRoles(roles);
  }

  @LogMethod()
  public getAll(): Observable<IUser[]> {
    return this.userService.getAllUsers();
  }

  @LogMethod()
  public loadCurrentUser(): void {
    this.store.dispatch(UserActions.loadCurrentUser());
  }

  public loadUser(userId?: UserId): void {
    if (userId) {
      this.store.dispatch(UserActions.loadUser({ userId }));
      return;
    }

    this.loadCurrentUser();
  }
}
